/**
 * The shared RegisterView component (Wave F-2B, CV-06).
 *
 * Centrally manages the register grid, sticky header, bounded viewport,
 * and skeleton / absence / refusal / empty states with slots:
 * - toolbar
 * - chart
 * - columns
 * - pagination
 * - children (table body rows)
 *
 * Adheres strictly to UX_SPEC.md Section 2 (Register anatomy).
 */

import { isValidElement, type ReactNode } from "react";
import { SkeletonRows, AbsencePanel, EmptyState } from "../screens/parts";
import { strings } from "../brand/strings";
import "../brand/components.css";

export interface RegisterViewProps {
  /** The toolbar slot: search input and standalone filters */
  readonly toolbar?: ReactNode;
  /** The lead chart slot: sits above the table */
  readonly chart?: ReactNode;
  /** The column headers slot */
  readonly columns: ReactNode;
  /** The table body slot: table row elements */
  readonly children?: ReactNode;
  /** The pagination slot: sits outside the bounded viewport */
  readonly pagination?: ReactNode;
  /** Loading state flag: displays the skeleton */
  readonly loading?: boolean;
  /** Absence state flag (the predicate returned an absence) */
  readonly absence?: boolean;
  /** Failure state flag (query failed) */
  readonly failed?: boolean;
  /** Empty state flag (query ready but 0 items) */
  readonly empty?: boolean;
  /** Empty state title */
  readonly emptyTitle?: string;
  /** Empty state body */
  readonly emptyBody?: string;
  /** Custom empty state node override */
  readonly emptyState?: ReactNode;
  /** Optional table caption */
  readonly caption?: string;
  /** Test ID for the <table> element */
  readonly testId?: string;
  /** Number of skeleton rows to display while loading (default 8) */
  readonly skeletonRows?: number;
  /** Number of skeleton columns to display while loading (default 5) */
  readonly skeletonColumns?: number;
  /** Optional container class name */
  readonly className?: string;
}

export function RegisterView({
  toolbar,
  chart,
  columns,
  children,
  pagination,
  loading = false,
  absence = false,
  failed = false,
  empty = false,
  emptyTitle,
  emptyBody,
  emptyState,
  caption,
  testId,
  skeletonRows = 8,
  skeletonColumns = 5,
  className,
}: RegisterViewProps): React.JSX.Element {
  return (
    <div className={className ? `q-register-view ${className}` : "q-register-view"}>
      {toolbar}
      {chart}
      {loading && <SkeletonRows rows={skeletonRows} columns={skeletonColumns} />}
      {absence && <AbsencePanel />}
      {failed && (
        <div className="q-refusal" role="alert">
          <p className="q-refusal__heading">{strings.common.failed}</p>
        </div>
      )}
      {!loading && !absence && !failed && (
        <>
          {empty ? (
            (emptyState ??
            (emptyBody !== undefined ? (
              <EmptyState title={emptyTitle ?? ""} body={emptyBody} />
            ) : (
              <EmptyState title={emptyTitle ?? ""} />
            )))
          ) : (
            <>
              <div className="q-register-viewport">
                <table className="q-register" data-testid={testId}>
                  {caption && <caption>{caption}</caption>}
                  <thead>
                    {isValidElement(columns) && columns.type === "tr" ? (
                      columns
                    ) : (
                      <tr>{columns}</tr>
                    )}
                  </thead>
                  <tbody>{children}</tbody>
                </table>
              </div>
              {pagination}
            </>
          )}
        </>
      )}
    </div>
  );
}
